import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ibsys/kit';
import { CornerUpLeftIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/shared/lib';
import type { ForwardedMeta } from '../model/htmlContent';

type Props = {
  /** Отправитель, дата, получатель и тема пересланного письма */
  meta: ForwardedMeta;
  /** Тело пересланного письма */
  children: ReactNode;
};

/** Поля пересланного письма в раскрытом виде, по порядку */
const META_FIELDS = [
  { field: 'from', labelKey: 'dialogs:textFeed.forwarded.from' },
  { field: 'sent', labelKey: 'dialogs:textFeed.forwarded.sent' },
  { field: 'to', labelKey: 'dialogs:textFeed.forwarded.to' },
  { field: 'subject', labelKey: 'dialogs:textFeed.forwarded.subject' },
] as const satisfies readonly { field: keyof ForwardedMeta; labelKey: string }[];

/**
 * Пересланная переписка внутри сообщения: свёрнута до отправителя и даты, раскрывается нажатием —
 * поля письма и его тело.
 */
export const ForwardedMessage: FC<Props> = ({ meta, children }) => {
  const { t } = useTranslation(['dialogs']);
  // Дату, которую браузер не прочёл, письмо показывает своей записью
  const sentAt = meta.sent ? formatDateTime(meta.sent) || meta.sent : null;

  return (
    <Accordion type="single" collapsible className="my-2 not-italic">
      <AccordionItem value="forwarded" className="rounded-md border border-border bg-muted/50">
        <AccordionTrigger
          className="gap-2 px-3 py-1.5 text-xs text-muted-foreground"
          aria-label={t('dialogs:textFeed.forwarded.toggle')}
        >
          {/* В узком облаке дата уходит на следующую строку, а отправитель переносится, а не сокращается */}
          <span className="flex min-w-0 items-center gap-2">
            <CornerUpLeftIcon className="size-3.5 shrink-0" />
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
              {meta.from && <span className="min-w-0 wrap-break-word font-semibold text-foreground">{meta.from}</span>}
              {sentAt && <span className="shrink-0">{sentAt}</span>}
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-2 border-t border-border px-3 py-2">
          <div className="flex flex-col text-xs text-muted-foreground">
            {META_FIELDS.map(({ field, labelKey }) =>
              meta[field] ? <span key={field}>{`${t(labelKey)}: ${meta[field]}`}</span> : null,
            )}
          </div>
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
